<template>
  <div id="app">
    <div
      class="App-layout mdl-layout mdl-layout--fixed-header mdl-js-layout"
      id="app-layout"
    >
      <app-header />
      <main id="main" class="App-main mdl-layout__content">
        <div class="App-container mdl-grid mdl-grid--no-spacing">
          <div
            class="mdl-cell mdl-cell--1-col mdl-cell--hide-tablet mdl-cell--hide-phone"
          />
          <div
            class="App-content mdl-color-text--grey-800 mdl-cell mdl-cell--10-col"
          >
            <!-- 2FA gate: replaces stepper + content until the voter verifies -->
            <two-factor-auth
              v-if="twoFaRequired"
              @verified="onVerified"
            />

            <template v-else>
              <transition name="expand">
                <stepper v-if="dataLoaded && !isDone && !hasError" />
              </transition>
              <transition name="expand">
                <div v-if="dataLoaded">
                  <div class="flex flex--justify--center">
                    <div class="App-view-container">
                      <transition name="expand">
                        <router-view />
                      </transition>
                    </div>
                  </div>
                </div>
              </transition>
              <div v-if="!dataLoaded">
                <div class="aligner">
                  <div class="aligner-item">
                    <h5>Please wait a moment while the form loads</h5>
                    <div
                      class="mdl-spinner mdl-spinner--single-color mdl-js-spinner is-active"
                    ></div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </main>
      <div class="mdl-layout-spacer"></div>
      <app-footer v-if="!isPreviewMode" />
    </div>
  </div>
</template>

<script>
import {mapGetters, mapState, mapActions} from 'vuex';

import AppHeader from '@/components/Header';
import AppFooter from '@/components/Footer';
import Stepper from '@/components/Stepper';
import TwoFactorAuth from '@/components/TwoFactorAuth';

import api from '@/api';

export default {
  name: 'app',
  components: {
    AppHeader,
    AppFooter,
    Stepper,
    TwoFactorAuth
  },
  watch: {
    $route: function(route) {
      this.calcStep(route);
    }
  },
  computed: {
    ...mapGetters(['steps', 'error']),
    ...mapState(['twoFaRequired']),
    currentStepNumOnLoad() {
      return this.steps.pathing.indexOf(this.path);
    },
    currentValidStepNum() {
      return this.steps.current;
    },
    isOnValidStep() {
      return this.currentStepNumOnLoad <= this.currentValidStepNum;
    },
    isDone() {
      return this.$route.path === '/done';
    },
    hasError() {
      return Boolean(this.error.message);
    },
    isPreviewMode() {
      return this.mode === 'preview';
    }
  },
  methods: {
    ...mapActions('meeting', ['initializeMeeting']),
    ...mapActions([
      'setShortCode',
      'setTrackingCode',
      'setToken',
      'runLoadActions',
      'setTwoFaRequired'
    ]),
    ...mapActions([
      'setCurrentStep',
      'setNextStep',
      'setPreviousStep',
      'initPusher',
      'setPreviewMode'
    ]),
    ...mapActions('voter', ['markRsvp']),
    loadMeeting() {
      const params = {
        shortCode: this.shortCode,
        trackingCode: this.trackingCode,
        token: this.token
      };
      return this.initializeMeeting(params);
    },
    calcStep(route) {
      let currentStep =
        route.path === '/' ? 0 : this.steps.pathing.indexOf(route.path);
      // Set step in store
      this.setCurrentStep(currentStep);
      this.setPreviousStep(currentStep - 1);
      this.setNextStep(currentStep + 1);
    },
    validateCurrentStep() {
      if (this.isPreviewMode) {
        return;
      }

      if (this.currentStepNumOnLoad < 0 || !this.isOnValidStep) {
        window.location.href = '/';
      }
    },

    /**
     * Called by TwoFactorAuth after the voter successfully verifies.
     * The store token was already updated to the authorized JWT by verify2fa.
     * We reset the gate flag and retry loading the meeting with the new token.
     */
    async onVerified() {
      this.setTwoFaRequired(false);
      // Update the local token reference to the authorized JWT now in the store
      this.token = this.$store.state.token;
      await this.loadMeeting();
      this.dataLoaded = true;
      this.calcStep(this.$route);
      this.$nextTick(() => {
        if (window.componentHandler) componentHandler.upgradeAllRegistered();
      });
    }
  },
  data() {
    return {
      // Get the shortCode from the subdomain
      shortCode: window.location.hostname.split('.')[0],
      trackingCode: this.$route.query.code || '',
      token: this.$route.query.token || '',
      mode: this.$route.query.mode || '',
      path: this.$route.path.split('?')[0],
      loadAction: this.$route.query.action || null,
      dataLoaded: false
    };
  },
  beforeDestroy() {},
  created() {
    // Checks that we're starting on the proper step.
    // Needed if refresh app part-way through process.
    this.validateCurrentStep();

    // save preview mode flag in store
    if (this.isPreviewMode) {
      this.setPreviewMode(true);
      this.initPusher(this.shortCode);
    }

    // Set identity parameters to be passed onto API for getting meeting and voter data
    this.setShortCode(this.shortCode);
    this.setTrackingCode(this.trackingCode);
    this.setToken(this.token);

    // Make the call to API to load meeting details.
    this.loadMeeting()
      .then(() => {
        this.dataLoaded = true;
        this.calcStep(this.$route);
      })
      // Check if any automatic actions need to be run (eg. setting RSVP)
      .then(() => {
        if (this.loadAction) {
          return this.runLoadActions({
            shortCode: this.shortCode,
            token: this.token,
            action: this.loadAction
          });
        }
      })
      .then(() => {
        componentHandler.upgradeAllRegistered();
      });
  },
  mounted() {
    this.$events.$on('log-voter-action', (action, data) => {
      api.sendVoterAction(
        action,
        this.shortCode,
        this.trackingCode,
        data,
        this.token
      );
    });
  }
};
</script>

<style>
a {
  cursor: pointer;
  cursor: hand;
}

/* .App-layout {
} */
.App-main {
  flex-shrink: 0;
  display: flex;
}
.App-container {
  width: 100%;
  margin: 0 auto;
}
.App-content {
  border-radius: 2px;
  padding: 45px 56px;
}
.App-error-text-color {
  color: red;
}
.App-view-container {
  width: 100%;
  max-width: 850px;
}

/* Toggle list */
.toggle-list {
  border-radius: 4px;
  margin-bottom: 2rem;
  background-color: #ffffff;
}

/* Shrink app content on smaller screen sizes */
.App-layout.is-small-screen .App-content {
  padding: 6px 6px;
}
.is-small-screen .App-icon-top {
  font-size: 4rem;
}

/** Flex **/
.flex {
  display: flex;
}
.flex--row {
  flex-flow: row nowrap;
}
.flex--column {
  flex-flow: column nowrap;
}
.flex--center {
  align-items: center;
}
.flex--justify--center {
  justify-content: center;
}
.flex--space-between {
  justify-content: space-between;
}

/* Alignment */
.aligner {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.aligner-item {
  max-width: 50%;
}
.mdl-grid {
  margin: 0;
}
.align-text-center {
  text-align: center;
}

/** Type App Icon at the top of each component **/
.App-icon-top {
  font-size: 6rem;
}
.App-radio--item {
  margin: 5px;
}

.App-buttons {
  padding-top: 1.2rem;
  padding-bottom: 4px;
}

.App-buttons > * {
  margin-right: 10px;
}

.info {
  line-height: 25px;
  display: inline-flex;
}
.md-divider {
  display: block;
  border-top: 1px solid;
  border-color: rgba(0, 0, 0, 0.1);
  margin: 0;
}

.material-icons.large-icon {
  font-size: 70px;
}
.material-icons.medium-icon {
  font-size: 60px;
}
.material-icons.small-icon {
  font-size: 50px;
}

/* Screen issues */
.mdl-layout__container.has-scrolling-header .mdl-layout__content {
  overflow-y: visible;
  overflow-x: visible;
}

/* Prevent header from appearing above modal */
.mdl-layout__header {
  z-index: 1;
}

/* Transitions */
/* always present */
.expand-enter-active,
.expand-leave-active {
  padding: 1px 1px 0px 1px;
  transition: all 0.3s ease-in-out;
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  height: 0px;
  padding: 0px 5px;
  opacity: 0;
}

/* Enter and leave animations can use different */
/* durations and timing functions.              */
.slide-fade-enter-active {
  transition: all 0.2s ease;
}
.slide-fade-leave-active {
  transition: all 0.1s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateX(10px);
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/*
 * the following styles are auto-applied to elements with
 * v-transition="modal" when their visiblity is toggled
 * by Vue.js.
 *
 * You can easily play with the modal transition by editing
 * these styles.
 */

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}

.headline {
  line-height: 1.25;
  font-size: 26px;
}

/**
 * Overrides MDL's default blockquote styling by removing the before and after quotations
 */
blockquote {
  border-left: 3px solid #ccc;
  padding-left: 12px;
  left: -1em;
}
blockquote:before {
  content: none;
}
blockquote:after {
  content: none;
}

@media only screen and (max-width: 450px) {
  .App-icon-top {
    display: none;
  }
  .headline {
    margin-top: 8px;
    font-size: 22px;
  }
}

.gq-text__secondary {
  color: #666;
}
</style>
